import { Flex } from '@chakra-ui/layout'
import {
  addEdge,
  Background, Connection, Edge, ReactFlow, ReactFlowProvider, useEdgesState, useNodesState
} from '@xyflow/react'
import { useCallback, useEffect, useMemo } from 'react'
import 'reactflow/dist/style.css'
import { CargoOrganogramaResponse } from '../../../../../interfaces'
import { buildReactFlowGraphFromCargos } from './BuildReactFlowGraph'
import CustomNode from './CustomNode'

const nodeTypes = {
  custom: CustomNode
}

interface OrganogramaProps {
  cargos: CargoOrganogramaResponse[]
}

const OrganogramaFlow: React.FC<OrganogramaProps> = ({ cargos }) => {
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    return buildReactFlowGraphFromCargos(cargos)
  }, [cargos])

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes as any)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges as any)

  const onConnect = useCallback(
    (params: Edge | Connection) => setEdges((els) => addEdge(params, els)),
    [setEdges]
  )

  function applyHierarchyLayoutFromCargos(
    cargos: CargoOrganogramaResponse[],
    nodes: any[]
  ): any[] {
    const NODE_W = 420
    const NODE_H = 240
    const GAP_X = 80
    const GAP_Y = 80
    const GROUP_GAP_X = 220

    const byId = new Map<string, any>()
    nodes.forEach((node) => {
      byId.set(node.id, node)
    })

    const cargoById = new Map<string, CargoOrganogramaResponse>()
    const childrenById = new Map<string, string[]>()
    const parentsById = new Map<string, string[]>()

    function ensureMaps(id: string): void {
      if (!childrenById.has(id)) childrenById.set(id, [])
      if (!parentsById.has(id)) parentsById.set(id, [])
    }

    function walk(cargo: CargoOrganogramaResponse): void {
      cargoById.set(cargo.id, cargo)
      ensureMaps(cargo.id)

      const subs = cargo.subordinados ?? []

      for (let i = 0; i < subs.length; i++) {
        const sub = subs[i]

        cargoById.set(sub.id, sub)
        ensureMaps(sub.id)

        const currentChildren = childrenById.get(cargo.id) ?? []
        if (!currentChildren.includes(sub.id)) {
          currentChildren.push(sub.id)
          childrenById.set(cargo.id, currentChildren)
        }

        const currentParents = parentsById.get(sub.id) ?? []
        if (!currentParents.includes(cargo.id)) {
          currentParents.push(cargo.id)
          parentsById.set(sub.id, currentParents)
        }

        walk(sub)
      }
    }

    for (let i = 0; i < cargos.length; i++) {
      walk(cargos[i])
    }

    const allIds = Array.from(cargoById.keys())

    // raízes = cargos sem pais
    const roots: string[] = []
    for (let i = 0; i < allIds.length; i++) {
      const id = allIds[i]
      const parents = parentsById.get(id) ?? []
      if (!parents.length) {
        roots.push(id)
      }
    }

    // encontra componentes conexos para separar blocos visuais
    const undirectedAdj = new Map<string, string[]>()
    for (let i = 0; i < allIds.length; i++) {
      undirectedAdj.set(allIds[i], [])
    }

    childrenById.forEach((children, parentId) => {
      for (let i = 0; i < children.length; i++) {
        const childId = children[i]
        const a = undirectedAdj.get(parentId) ?? []
        const b = undirectedAdj.get(childId) ?? []

        if (!a.includes(childId)) a.push(childId)
        if (!b.includes(parentId)) b.push(parentId)

        undirectedAdj.set(parentId, a)
        undirectedAdj.set(childId, b)
      }
    })

    const visited = new Set<string>()
    const groups: string[][] = []

    for (let i = 0; i < allIds.length; i++) {
      const startId = allIds[i]
      if (visited.has(startId)) continue

      const group: string[] = []
      const stack = [startId]
      visited.add(startId)

      while (stack.length) {
        const current = stack.pop() as string
        group.push(current)

        const neighbors = undirectedAdj.get(current) ?? []
        for (let j = 0; j < neighbors.length; j++) {
          const nb = neighbors[j]
          if (!visited.has(nb)) {
            visited.add(nb)
            stack.push(nb)
          }
        }
      }

      groups.push(group)
    }

    groups.sort((a, b) => b.length - a.length)

    const laidOut = new Map<string, any>()
    let currentGroupOffsetX = 0

    for (let g = 0; g < groups.length; g++) {
      const groupIds = groups[g]
      const groupSet = new Set(groupIds)

      // indegree apenas dentro do grupo
      const indegree = new Map<string, number>()
      for (let i = 0; i < groupIds.length; i++) {
        indegree.set(groupIds[i], 0)
      }

      for (let i = 0; i < groupIds.length; i++) {
        const parentId = groupIds[i]
        const children = childrenById.get(parentId) ?? []

        for (let j = 0; j < children.length; j++) {
          const childId = children[j]
          if (!groupSet.has(childId)) continue
          indegree.set(childId, (indegree.get(childId) ?? 0) + 1)
        }
      }

      // Kahn: ordem topológica
      const queue: string[] = []
      for (let i = 0; i < groupIds.length; i++) {
        const id = groupIds[i]
        if ((indegree.get(id) ?? 0) === 0) {
          queue.push(id)
        }
      }

      const topoOrder: string[] = []
      while (queue.length) {
        const current = queue.shift() as string
        topoOrder.push(current)

        const children = childrenById.get(current) ?? []
        for (let i = 0; i < children.length; i++) {
          const childId = children[i]
          if (!groupSet.has(childId)) continue

          const next = (indegree.get(childId) ?? 0) - 1
          indegree.set(childId, next)

          if (next === 0) {
            queue.push(childId)
          }
        }
      }

      // fallback para casos estranhos/cíclicos
      for (let i = 0; i < groupIds.length; i++) {
        const id = groupIds[i]
        if (!topoOrder.includes(id)) {
          topoOrder.push(id)
        }
      }

      // nível = max(nível dos pais) + 1
      const levelById = new Map<string, number>()

      for (let i = 0; i < topoOrder.length; i++) {
        const id = topoOrder[i]
        const parents = (parentsById.get(id) ?? []).filter((p) => groupSet.has(p))

        if (!parents.length) {
          levelById.set(id, 0)
          continue
        }

        let maxParentLevel = 0
        for (let j = 0; j < parents.length; j++) {
          const parentLevel = levelById.get(parents[j]) ?? 0
          if (parentLevel > maxParentLevel) {
            maxParentLevel = parentLevel
          }
        }

        levelById.set(id, maxParentLevel + 1)
      }

      // agrupa por nível
      const levels = new Map<number, string[]>()
      for (let i = 0; i < groupIds.length; i++) {
        const id = groupIds[i]
        const level = levelById.get(id) ?? 0

        if (!levels.has(level)) levels.set(level, [])
        ;(levels.get(level) as string[]).push(id)
      }

      const sortedLevels = Array.from(levels.keys()).sort((a, b) => a - b)

      // ordenação horizontal:
      // tenta manter o node perto da média horizontal dos pais
      const orderIndexById = new Map<string, number>()

      for (let i = 0; i < sortedLevels.length; i++) {
        const level = sortedLevels[i]
        const ids = levels.get(level) ?? []

        ids.sort((a, b) => {
          const scoreA = getParentBarycenter(a, parentsById, orderIndexById)
          const scoreB = getParentBarycenter(b, parentsById, orderIndexById)

          if (scoreA !== scoreB) return scoreA - scoreB

          const childrenA = (childrenById.get(a) ?? []).length
          const childrenB = (childrenById.get(b) ?? []).length
          return childrenB - childrenA
        })

        for (let j = 0; j < ids.length; j++) {
          orderIndexById.set(ids[j], j)
        }
      }

      let maxCols = 0
      for (let i = 0; i < sortedLevels.length; i++) {
        const level = sortedLevels[i]
        const count = (levels.get(level) ?? []).length
        if (count > maxCols) maxCols = count
      }

      for (let i = 0; i < sortedLevels.length; i++) {
        const level = sortedLevels[i]
        const ids = levels.get(level) ?? []
        const leftOffsetCols = (maxCols - ids.length) / 2

        for (let j = 0; j < ids.length; j++) {
          const id = ids[j]
          const node = byId.get(id)
          if (!node) continue

          laidOut.set(id, {
            ...node,
            position: {
              x: currentGroupOffsetX + (leftOffsetCols + j) * (NODE_W + GAP_X),
              y: level * (NODE_H + GAP_Y)
            }
          })
        }
      }

      currentGroupOffsetX += maxCols * (NODE_W + GAP_X) + GROUP_GAP_X
    }

    return nodes.map((node) => laidOut.get(node.id) ?? node)
  }

  function getParentBarycenter(
    nodeId: string,
    parentsById: Map<string, string[]>,
    orderIndexById: Map<string, number>
  ): number {
    const parents = parentsById.get(nodeId) ?? []

    const knownIndexes: number[] = []
    for (let i = 0; i < parents.length; i++) {
      const idx = orderIndexById.get(parents[i])
      if (typeof idx === 'number') {
        knownIndexes.push(idx)
      }
    }

    if (!knownIndexes.length) return Number.MAX_SAFE_INTEGER

    let sum = 0
    for (let i = 0; i < knownIndexes.length; i++) {
      sum += knownIndexes[i]
    }

    return sum / knownIndexes.length
  }

  useEffect(() => {
    setNodes(applyHierarchyLayoutFromCargos(cargos, initialNodes as any))
    setEdges(initialEdges)
  }, [cargos, initialNodes, initialEdges, setNodes, setEdges])

  return (
    <Flex flexDir="column" gap="1rem" alignItems="end">
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes as any}
          minZoom={0.05}
          fitView
          style={{
            minHeight: '78vh',
            maxHeight: '78vh',
            border: '1px solid var(--c6)'
          }}
          defaultEdgeOptions={{
            style: { stroke: '#1961E0', strokeWidth: 2 }
          }}
        >
          <Background />
        </ReactFlow>
      </ReactFlowProvider>
    </Flex>
  )
}

export default OrganogramaFlow
